import {
  AnnouncementByTenantLandscapeQueryVariables,
  _Boolean,
  ModelIntKeyConditionInput,
} from '@/API';
import {
  AnnouncementByTenantLandscapeQuery,
  announcementByTenantLandscapeWithConnection,
} from '@/appsync/graphql/announcement';
import {
  UserAnnouncementType,
  UserAnnouncementQuery,
  UserAnnouncementQueryVariables,
  GetUserAnnouncementQuery,
  GetUserAnnouncementQueryVariables,
  getUserAnnouncements,
  getUserAnnouncement,
} from '@/appsync/graphql/userAnnouncement';
import { graphqlQuery } from '@/appsync/service/GraphqlUtils';
import {
  LoginInfo,
  Announcement,
  UserAnnouncement,
  UserAnnouncementItemWithPagination,
  UserAnnouncementQueryParam,
} from '@/types/type';
import {
  getTimeOfToday,
  getTimeOfTomorrow,
  canShow,
  convertUserAnnouncement,
} from '@/utils/AnnouncementUtils';
import { getSHA3of, _BooleanEnum2boolean } from '@/utils/CommonUtils';

type AnnouncementItems = NonNullable<
  AnnouncementByTenantLandscapeQuery['announcementByTenantLandscape']
>['items'];

export const fetchAnnouncements = async (
  tenant: string,
  landscape: string,
  nextToken?: string
): Promise<Announcement[]> => {
  const announcements: Announcement[] = [];
  let isFirstExecution = true;
  const tenantLandscape = getSHA3of(`${tenant}#${landscape}`);
  while (isFirstExecution || nextToken) {
    isFirstExecution = false;
    const variables: AnnouncementByTenantLandscapeQueryVariables = {
      tenantLandscape,
      nextToken,
    };

    const result = await graphqlQuery<
      AnnouncementByTenantLandscapeQuery,
      AnnouncementByTenantLandscapeQueryVariables
    >(announcementByTenantLandscapeWithConnection, variables);

    if (
      result &&
      result.announcementByTenantLandscape &&
      result.announcementByTenantLandscape.items
    ) {
      const items = eliminateDbDependency(
        result.announcementByTenantLandscape.items
      );

      announcements.push(...items);
    }
    if (
      result &&
      result.announcementByTenantLandscape &&
      result.announcementByTenantLandscape.nextToken
    ) {
      nextToken = result.announcementByTenantLandscape.nextToken;
    } else {
      break;
    }
  }
  return announcements;
};

const eliminateDbDependency = (
  announcements: AnnouncementItems
): Announcement[] =>
  !announcements
    ? []
    : announcements.map(val =>
        !val
          ? ({} as Announcement)
          : {
              ...val,
              isPublic: _BooleanEnum2boolean(val.isPublic),
              displayStartDate: val.displayStartDate,
              displayEndDate:
                val.displayEndDate === 0 || val.displayEndDate == null
                  ? null
                  : val.displayEndDate,
              updatedAt: val.updatedAt != null ? val.updatedAt : null,
            }
      );

const MAX_LIMIT_NUMBER = 10;

export const fetchUserAnnouncements = async (
  param: LoginInfo,
  nextToken?: string,
  limit?: number
): Promise<UserAnnouncementItemWithPagination> => {
  const preNextToken = nextToken;
  const userAnnouncements: UserAnnouncement[] = [];
  const tenantLandscapeLoginName = getSHA3of(
    `${param.tenant}#${param.landscape}#${param.loginName}`
  );
  const displayStartDate: ModelIntKeyConditionInput = {
    lt: getTimeOfTomorrow(),
  };

  let len = 0;
  if (!limit) {
    limit = MAX_LIMIT_NUMBER;
  }
  while (len < limit) {
    const variables: UserAnnouncementQueryVariables = {
      tenantLandscapeLoginName,
      displayStartDate,
      limit: MAX_LIMIT_NUMBER,
      nextToken,
    };
    const result = await graphqlQuery<
      UserAnnouncementQuery,
      UserAnnouncementQueryVariables
    >(getUserAnnouncements, variables);
    if (result && result.userAnnouncements && result.userAnnouncements.items) {
      const filteredUserAnnouncements: UserAnnouncement[] = userAnnouncementsByDisplayStartDate(
        result.userAnnouncements.items
      );
      userAnnouncements.push(...filteredUserAnnouncements);
      len += filteredUserAnnouncements.length;
    }

    if (
      result &&
      result.userAnnouncements &&
      result.userAnnouncements.nextToken
    ) {
      nextToken = result.userAnnouncements.nextToken;
    } else if (result && result.userAnnouncements) {
      nextToken = result.userAnnouncements.nextToken;
      break;
    } else {
      break;
    }
  }

  if (preNextToken === nextToken || !nextToken) {
    nextToken = undefined;
  }

  return {
    items: userAnnouncements,
    nextToken: nextToken,
  };
};

const userAnnouncementsByDisplayStartDate = (
  userAnnouncements: UserAnnouncementType[]
): UserAnnouncement[] => {
  return userAnnouncements
    .filter(
      (userAnnouncement: UserAnnouncementType) =>
        _BooleanEnum2boolean(userAnnouncement.isShow) &&
        (userAnnouncement.displayEndDate >= getTimeOfToday() ||
          userAnnouncement.displayEndDate === 0)
    )
    .map(
      (userAnnouncement: UserAnnouncementType): UserAnnouncement => {
        return convertUserAnnouncement(userAnnouncement);
      }
    );
};

export const fetchUserAnnouncement = async (
  param: UserAnnouncementQueryParam
): Promise<UserAnnouncement | null> => {
  const variables: GetUserAnnouncementQueryVariables = {
    loginName: param.loginName,
    tenant: param.tenant,
    landscape: param.landscape,
    announcementId: param.id,
  };

  const result = await graphqlQuery<
    GetUserAnnouncementQuery,
    GetUserAnnouncementQueryVariables
  >(getUserAnnouncement, variables);
  if (result && result.userAnnouncement) {
    return canShow(result.userAnnouncement)
      ? convertUserAnnouncement(result.userAnnouncement)
      : null;
  }
  return null;
};
